import { cs } from '../../../lib/currency';
/**
 * iter307cw — Admin Credit Ledger Summary page.
 *
 * Lists all parents with a non-zero credit balance (default), sorted
 * by balance desc. Click the parent name to open their full profile
 * (where the detailed credit history lives on the Billing tab).
 *
 * All columns are sortable via the shared <SortableTable> primitive.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import SortableTable from '../../../components/ui/sortableTable';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Wallet } from 'lucide-react';

const fmtUsd = (n) => `${cs()}${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return '—';
  }
};

const CreditLedgerPage = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [asOf, setAsOf] = useState(null);
  const [unattributed, setUnattributed] = useState({ count: 0, balance: 0 });
  const [includeZero, setIncludeZero] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (showToast = false) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/credit-ledger/summary', {
        params: { include_zero: includeZero },
      });
      setRows(data?.rows || []);
      setCount(data?.count || 0);
      setTotalBalance(data?.total_balance || 0);
      setAsOf(data?.as_of || null);
      setUnattributed({
        count: data?.unattributed_count || 0,
        balance: data?.unattributed_balance || 0,
      });
      if (showToast) toast.success(`Loaded ${data?.count || 0} parents with credit balance.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load credit ledger.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeZero]);

  // iter307cy — Route the parent-name link based on the user's role
  // so opening a Customer's profile loads CustomerDetail (not the
  // ParentDetail page) and Coaches go to the Coach profile. Falls
  // back to /admin/parents/:id for the generic parent role.
  const profilePath = (row) => {
    const role = String(row?.role || '').toLowerCase();
    if (role === 'coach') return `/admin/coaches/${row.user_id}`;
    if (role === 'customer') return `/admin/customers/${row.user_id}`;
    return `/admin/parents/${row.user_id}`;
  };

  const columns = useMemo(() => ([
    {
      key: 'full_name',
      label: 'Parent / Customer',
      sortable: true,
      render: (r) => (
        r.user_id ? (
          <Link
            to={profilePath(r)}
            className="text-blue-600 hover:underline font-medium"
            data-testid={`credit-ledger-name-${r.user_id}`}
          >
            {r.full_name}
          </Link>
        ) : (
          <span className="text-slate-500 font-medium">{r.full_name}</span>
        )
      ),
    },
    { key: 'email', label: 'Email', sortable: true, render: (r) => r.email || '—' },
    { key: 'phone', label: 'Phone', sortable: true, render: (r) => r.phone || '—' },
    {
      key: 'balance',
      label: 'Credit Balance',
      sortable: true,
      align: 'right',
      type: 'numeric',
      render: (r) => (
        <span
          className={r.balance > 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}
          data-testid={`credit-ledger-balance-${r.user_id}`}
        >
          {fmtUsd(r.balance)}
        </span>
      ),
    },
    {
      key: 'last_credit_at',
      label: 'Last Credit Activity',
      sortable: true,
      type: 'date',
      render: (r) => fmtDate(r.last_credit_at),
    },
    {
      key: 'updated_at',
      label: 'Balance Updated',
      sortable: true,
      type: 'date',
      render: (r) => fmtDate(r.updated_at),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (r) => r.status ? <Badge variant="outline" className="capitalize">{r.status}</Badge> : '—',
    },
  ]), []);

  return (
    <DashboardLayout title="Credit Ledger">
      <div className="p-6 space-y-4" data-testid="credit-ledger-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-600" />
            Credit Ledger
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Account credit balances for every customer / parent / coach. Click a name to open their profile (role-aware routing).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={loading}
          data-testid="credit-ledger-refresh"
        >
          {loading
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Users w/ Credit</p>
            <p className="text-2xl font-bold mt-1" data-testid="credit-ledger-count">{count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Outstanding Credit</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="credit-ledger-total">
              {fmtUsd(totalBalance)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">As of</p>
            <p className="text-sm font-medium mt-1" data-testid="credit-ledger-asof">{fmtDate(asOf)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Unattributed credit warning */}
      {unattributed.count > 0 && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="credit-ledger-unattributed"
        >
          <strong>{unattributed.count}</strong> orphaned credit record{unattributed.count === 1 ? '' : 's'}
          {' '}totaling <strong>{fmtUsd(unattributed.balance)}</strong> could not be matched to a current
          profile (likely from merged or removed accounts) and are hidden from the list below. Run credit
          reconciliation to clean these up.
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4 pb-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-zero"
              checked={includeZero}
              onCheckedChange={(v) => setIncludeZero(!!v)}
              data-testid="credit-ledger-include-zero"
            />
            <Label htmlFor="include-zero" className="text-sm cursor-pointer">
              Show $0 balances too (useful for spotting data drift)
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {count} user{count === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-12" data-testid="credit-ledger-empty">
              {includeZero
                ? 'No users have any recorded credit balance.'
                : 'No users currently hold a positive credit balance.'}
            </p>
          ) : (
            <SortableTable
              data={rows}
              columns={columns}
              defaultSort={{ field: 'balance', direction: 'desc' }}
              pageSize={50}
              getRowKey={(r, i) => r.user_id || `row-${i}`}
              testidPrefix="credit-ledger"
            />
          )}
        </CardContent>
      </Card>
      </div>
    </DashboardLayout>
  );
};

export default CreditLedgerPage;
